import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  ChevronDown,
  ChevronsLeft,
  LayoutDashboard,
  GraduationCap,
  Users,
  CalendarDays,
  ClipboardList,
  Wallet,
  Settings,
  Bell,
  BookOpen,
  BarChart3,
  X,
  Building2,
} from "lucide-react";
import { useToast } from "../../context/ToastContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const api = axios.create({ baseURL: API, withCredentials: true });

// ---------- Demo data ----------
const DEMO_NAV = [
  {
    category: "Overview",
    items: [
      { title: "Dashboard", url: "/", icon: LayoutDashboard },
      { title: "Analytics", url: "/analytics", icon: BarChart3 },
    ],
  },
  {
    category: "Academics",
    items: [
      {
        title: "Students",
        icon: GraduationCap,
        children: [
          { title: "All students", url: "/students" },
          { title: "Admissions", url: "/students/admissions" },
          { title: "Attendance", url: "/students/attendance" },
        ],
      },
      {
        title: "Classes",
        icon: BookOpen,
        isClassTeacherOnly: true,
        children: [
          { title: "Timetable", url: "/classes/timetable" },
          { title: "Assignments", url: "/classes/assignments" },
        ],
      },
      { title: "Staff", url: "/staff", icon: Users },
      { title: "Calendar", url: "/calendar", icon: CalendarDays },
    ],
  },
  {
    category: "Operations",
    items: [
      {
        title: "Fees & billing",
        url: "/billing",
        icon: Wallet,
        permission: "billing",
      },
      { title: "Reports", url: "/reports", icon: ClipboardList },
      {
        title: "Notifications",
        url: "/notifications",
        icon: Bell,
        badge: 3,
      },
      { title: "Settings", url: "/settings", icon: Settings },
    ],
  },
];

// SaaS branding only (your product) — school logo is loaded dynamically
const DEFAULT_SAAS = {
  name: "",
  logoUrl:
    "https://res.cloudinary.com/hsvfv0h8/image/upload/v1786152037/Untitled_design__6_-removebg-preview_zijzg4.png",
};

// ---------- Logo with graceful fallback ----------
const LogoImage = ({
  src,
  alt,
  size = "h-7 w-7 sm:h-8 sm:w-8",
  rounded = "rounded-md",
  fallbackIcon: FallbackIcon = Building2,
}) => {
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setErrored(false);
  }, [src]);

  if (!src || errored) {
    return (
      <span
        className={`flex ${size} shrink-0 items-center justify-center ${rounded} border border-neutral-200 bg-neutral-100 text-neutral-400 dark:border-neutral-800 dark:bg-neutral-800 dark:text-neutral-500`}
      >
        <FallbackIcon className="h-4 w-4" strokeWidth={2} />
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt || "Logo"}
      onError={() => setErrored(true)}
      className={`${size} shrink-0 ${rounded} object-contain bg-transparent`}
    />
  );
};

const CollapsedTooltip = ({ label }) => (
  <span
    role="tooltip"
    className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2.5 py-1.5 text-label-sm font-medium text-white opacity-0 shadow-popover transition-opacity duration-150 group-hover:opacity-100 dark:shadow-black/40"
  >
    {label}
  </span>
);

const checkPermission = (itemPermission, userPermissions) => {
  if (!itemPermission) return true;

  if (Array.isArray(itemPermission)) {
    return itemPermission.some((p) => userPermissions.includes(p));
  }

  return userPermissions.includes(itemPermission);
};

const NavItem = ({
  item,
  level = 1,
  isCollapsed,
  userPermissions = [],
  isClassTeacher = false,
  onNavigate,
}) => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const hasPermission = checkPermission(item.permission, userPermissions);
  const classTeacherCheck = !item.isClassTeacherOnly || isClassTeacher;
  const isAllowed = hasPermission && classTeacherCheck;

  const isActive = item.url ? pathname === item.url : false;
  const isChildActive =
    hasChildren && item.children.some((c) => c.url === pathname);

  const Icon = item.icon;

  useEffect(() => {
    if (isChildActive) setIsOpen(true);
  }, [isChildActive]);

  if (!isAllowed) return null;

  /*
   * Minimal navigation styling:
   * - No gradient active indicator
   * - Hover treatment lives ONLY on the row (Link/button). The icon and
   *   label simply inherit/react to that single hover state via `group-*`
   *   color utilities — they no longer paint their own background, which
   *   was previously causing a "double hover" effect (row bg + icon bg).
   * - Active state uses a subtle neutral/primary background
   */
  const rowBase =
    "group relative flex w-full items-center gap-2.5 rounded-lg py-2 pr-3 text-body-sm font-medium outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1";

  const paddingLeft =
    level === 1 ? "pl-3" : level === 2 ? "pl-8" : "pl-11";

  const active = isActive || isChildActive;

  const content = (
    <>
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors duration-150 ${
          isCollapsed ? "mx-auto" : ""
        } ${
          active
            ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
            : "text-neutral-400 group-hover:text-neutral-600 dark:text-neutral-500 dark:group-hover:text-neutral-300"
        }`}
      >
        {Icon ? (
          <Icon className="h-4 w-4" strokeWidth={2.25} />
        ) : (
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              active
                ? "bg-primary-500"
                : "bg-neutral-300 dark:bg-neutral-600"
            }`}
          />
        )}
      </span>

      {!isCollapsed && (
        <span
          className={`min-w-0 flex-1 truncate text-left ${
            active
              ? "font-semibold text-primary-800 dark:text-primary-300"
              : "text-neutral-600 dark:text-neutral-300 group-hover:text-neutral-800 dark:group-hover:text-neutral-100"
          }`}
        >
          {item.title}
        </span>
      )}

      {!isCollapsed && typeof item.badge === "number" && (
        <span className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-white">
          {item.badge}
        </span>
      )}

      {!isCollapsed && hasChildren && (
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-neutral-400 transition-transform duration-200 dark:text-neutral-500 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      )}

      {isCollapsed && <CollapsedTooltip label={item.title} />}
    </>
  );

  return (
    <div className="w-full select-none">
      {hasChildren ? (
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => !isCollapsed && setIsOpen((o) => !o)}
          className={`${rowBase} ${paddingLeft} ${
            active
              ? "bg-primary-50 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300"
              : "text-neutral-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800/70"
          } ${isCollapsed ? "justify-center px-0" : ""}`}
        >
          {content}
        </button>
      ) : (
        <Link
          to={item.url || "#"}
          onClick={onNavigate}
          aria-current={isActive ? "page" : undefined}
          className={`${rowBase} ${paddingLeft} ${
            active
              ? "bg-primary-50 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300"
              : "text-neutral-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800/70"
          } ${isCollapsed ? "justify-center px-0" : ""}`}
        >
          {content}
        </Link>
      )}

      {hasChildren && !isCollapsed && (
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-out"
          style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
        >
          <div className="ml-6 overflow-hidden border-l border-neutral-200 pl-1.5 dark:border-neutral-800">
            {isOpen &&
              item.children.map((child, idx) => (
                <NavItem
                  key={idx}
                  item={child}
                  level={level + 1}
                  isCollapsed={isCollapsed}
                  userPermissions={userPermissions}
                  isClassTeacher={isClassTeacher}
                  onNavigate={onNavigate}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Detect which panel the user is on so we hit the correct branding endpoint
 * (each uses a different auth cookie: owner / staffToken / studentToken).
 */
function detectPanelMode(isPortalProp) {
  if (typeof window === "undefined") {
    return isPortalProp ? "staff" : "owner";
  }

  const path = window.location.pathname || "";

  // Student panel
  if (
    path.startsWith("/student-panel") ||
    path.startsWith("/student-home") ||
    path.startsWith("/student/")
  ) {
    return "student";
  }

  // Staff panel (includes /staff-home which is NOT under /staff-panel)
  if (
    isPortalProp ||
    path.startsWith("/staff-panel") ||
    path.startsWith("/staff-home") ||
    path.startsWith("/staff/")
  ) {
    return "staff";
  }

  return "owner";
}

/**
 * Fetch school branding (name + logo) for the logged-in school.
 * Owner:   GET /school/me
 * Staff:   GET /staff-portal/branding (fallback: /school-portal/branding)
 * Student: GET /student-dashboard/branding
 */
async function fetchSchoolBranding(panelMode) {
  if (panelMode === "staff") {
    // Prefer staff-portal (same family as other portal APIs);
    // fall back to school-portal for older deployments.
    let d = null;

    try {
      const res = await api.get("/staff-portal/branding");
      d = res.data || {};
    } catch (err) {
      if (err?.response?.status === 404) {
        const res = await api.get("/school-portal/branding");
        d = res.data || {};
      } else {
        throw err;
      }
    }

    return {
      name: d.schoolName || "",
      logoUrl: d.logo?.url || null,
    };
  }

  if (panelMode === "student") {
    const res = await api.get("/student-dashboard/branding");
    const d = res.data || {};

    return {
      name: d.schoolName || "",
      logoUrl: d.logo?.url || null,
    };
  }

  // Owner / principal
  const res = await api.get("/school/me");
  const school = res.data || {};

  return {
    name: school.schoolName || "",
    logoUrl: school.logo?.url || null,
  };
}

export default function Sidebar({
  navData,
  userPermissions = ["billing"],
  isClassTeacher = true,
  isCollapsed: externalCollapsed,
  onCollapseToggle,
  isMobileOpen = false,
  onMobileClose,
  /** Optional override — if name/logoUrl provided, skip fetch */
  tenant: tenantProp,
  saas = DEFAULT_SAAS,
  /** true on staff (and student) panel */
  isPortal = false,
}) {
  const data = navData?.length ? navData : DEMO_NAV;

  const [internalCollapsed, setInternalCollapsed] = useState(false);

  const isCollapsed =
    externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;

  // Dynamic school branding from School schema
  const [tenant, setTenant] = useState({
    name: tenantProp?.name || tenantProp?.schoolName || "",
    logoUrl: tenantProp?.logoUrl || tenantProp?.logo?.url || null,
  });

  useEffect(() => {
    // Prefer explicit prop when parent already has school data
    if (
      tenantProp?.logoUrl ||
      tenantProp?.logo?.url ||
      tenantProp?.name ||
      tenantProp?.schoolName
    ) {
      setTenant({
        name: tenantProp.name || tenantProp.schoolName || "",
        logoUrl: tenantProp.logoUrl || tenantProp.logo?.url || null,
      });

      return;
    }

    const panelMode = detectPanelMode(isPortal);

    let cancelled = false;

    (async () => {
      try {
        const branding = await fetchSchoolBranding(panelMode);

        if (!cancelled) {
          setTenant(branding);
        }
      } catch (err) {
        console.warn(
          "School branding fetch failed:",
          panelMode,
          err?.response?.status || err?.message
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantProp, isPortal]);

  const handleCollapseToggle = () => {
    const next = !isCollapsed;

    if (externalCollapsed === undefined) {
      setInternalCollapsed(next);
    }

    onCollapseToggle?.(next);
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onMobileClose?.();

    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [onMobileClose]);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileOpen]);

  return (
    <>
      <div
        onClick={onMobileClose}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-neutral-900/40 backdrop-blur-[2px] transition-opacity duration-300 lg:hidden ${
          isMobileOpen
            ? "opacity-100"
            : "pointer-events-none opacity-0"
        }`}
      />

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-screen flex-col border-r border-neutral-200 bg-white transition-[transform,width] duration-300 ease-out dark:border-neutral-800 dark:bg-neutral-900 w-[17rem] ${
          isCollapsed ? "lg:w-20" : "lg:w-64"
        } ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        {/* Header: SCHOOL logo + name (from School schema) */}
        <div
          className={`flex h-14 shrink-0 items-center border-b border-neutral-100 px-4 dark:border-neutral-800 sm:h-16 ${
            isCollapsed ? "justify-center" : "justify-between"
          }`}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <LogoImage
              src={tenant.logoUrl}
              alt={tenant.name || "School"}
              size="h-12 sm:h-12"
              rounded="rounded-lg"
            />

            {!isCollapsed && (
              <span className="truncate font-display text-sm font-semibold text-neutral-900 dark:text-neutral-100 sm:text-title-sm">
                {tenant.name || "School"}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={onMobileClose}
            aria-label="Close navigation"
            className="rounded-lg p-1.5 text-neutral-400 outline-none transition-colors hover:bg-neutral-100 hover:text-neutral-600 focus-visible:ring-2 focus-visible:ring-primary-400 dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 lg:hidden"
          >
            <X size={18} />
          </button>
        </div>

        <nav
          className="flex-1 space-y-5 overflow-y-auto px-3 py-4"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>{`
            nav::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {data.map((section, idx) => (
            <div key={idx} className="space-y-1">
              {!isCollapsed && (
                <h3 className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 sm:text-label-sm">
                  {section.category}
                </h3>
              )}

              {isCollapsed && idx > 0 && (
                <div className="mx-3 mb-3 border-t border-neutral-100 dark:border-neutral-800" />
              )}

              <div className="space-y-0.5">
                {section.items.map((item, itemIdx) => (
                  <NavItem
                    key={itemIdx}
                    item={item}
                    isCollapsed={isCollapsed}
                    userPermissions={userPermissions}
                    isClassTeacher={isClassTeacher}
                    onNavigate={onMobileClose}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/*
          Footer: collapse control + "Powered by" attribution.
          Redesigned so it no longer looks like a stray gray chip:
          - No boxed background around the logo/text.
          - Logo + label sit in a simple, evenly spaced row (or stacked
            centered when collapsed), separated from the nav by a hairline
            border only — consistent with the rest of the sidebar's
            minimal styling.
        */}
        <div className="shrink-0 border-t border-neutral-100 p-3 dark:border-neutral-800">
          <div
            className={`flex items-center gap-2 py-1.5 ${
              isCollapsed ? "flex-col justify-center" : "justify-between"
            }`}
          >
            {/* Powered-by attribution: label+name sit to the LEFT of the logo */}
            <div
              className={`flex items-center gap-2 ${
                isCollapsed ? "flex-col" : ""
              }`}
            >
              {!isCollapsed && (
                <div className="flex flex-col leading-tight">
                  <span className="text-[9px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
                    Powered by
                  </span>
                  {saas.name ? (
                    <span className="truncate text-[11px] font-semibold text-neutral-500 dark:text-neutral-400">
                      {saas.name}
                    </span>
                  ) : null}
                </div>
              )}

              <LogoImage
                src={saas.logoUrl}
                alt={saas.name || "SaaS"}
                size="h-6 w-auto"
                rounded="rounded-none"
              />
            </div>

            {/* Collapse/expand toggle: icon only, no label text.
                Sits inline with the powered-by row (right-aligned when
                expanded, stacked below the logo when collapsed) instead
                of its own full-width bar. */}
            <button
              type="button"
              onClick={handleCollapseToggle}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 ${
                isCollapsed ? "mt-1" : ""
              }`}
            >
              <ChevronsLeft
                className={`h-4 w-4 transition-transform duration-200 ${
                  isCollapsed ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}